import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { Link as RouterLink, useParams } from "react-router-dom";

import {
  DetailShell,
  ScopeBadge,
  SectionCard,
  SkeletonTable,
  StatusPill,
} from "../../components/Directory/primitives";
import { dirTokens } from "../../components/Directory/tokens";
import {
  getAccount,
  getAccountGroups,
  getAccountMembers,
  inviteUserToAccount,
  removeAccountMember,
} from "./api";

/**
 * DirectoryAccountDetail — /directory/accounts/:accountId.
 *
 * Drill-down for an accounts-tab row. Renders:
 *   * Hero: account name + id + Beta chip + trial-status pill + max-projects
 *   * "+ Invite user" CTA in the actions slot
 *   * Two tabs (Users, Groups) below the hero, each with a
 *     SkeletonTable while loading and an EmptyState when the coord
 *     route is either empty or unavailable (404)
 *
 * Uses the shared DetailShell + SectionCard + StatusPill + ScopeBadge
 * primitives so the page matches UserDetail / RoleDetail visually.
 */

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function trialStatus(a) {
  if (!a) return { label: "Loading", color: "default" };
  if (a.billingEnabled) return { label: "Billing", color: "success" };
  const now = Date.now();
  const end = a.trialEndOn ? new Date(a.trialEndOn).getTime() : null;
  if (end === null) return { label: "No trial set", color: "default" };
  if (now > end) return { label: "Trial expired", color: "error" };
  const daysLeft = Math.max(0, Math.ceil((end - now) / 86_400_000));
  return {
    label: `Trial · ${daysLeft}d left`,
    color: daysLeft <= 7 ? "warning" : "default",
  };
}

const MEMBER_COLUMNS = [
  { label: "Email" },
  { label: "Name" },
  {
    label: "Role",
    width: 160,
    info: "Role held on this account (ACCOUNT_ADMIN, MEMBER). Per-project grants apply on top.",
  },
  {
    label: "Create projects",
    align: "center",
    width: 140,
    info: "Whether this member can create new projects inside the account. ACCOUNT_ADMINs always can.",
  },
  { label: "Added", width: 120 },
  { label: "", align: "right", width: 56 },
];

const GROUP_COLUMNS = [
  { label: "Name" },
  { label: "Description" },
  { label: "Members", align: "right", width: 100 },
  { label: "Source", width: 120 },
  { label: "Group ID", mono: true },
];

export default function DirectoryAccountDetail() {
  const { accountId } = useParams();

  const [account, setAccount] = useState(null);
  const [accountErr, setAccountErr] = useState(null);

  const [members, setMembers] = useState(null);
  const [membersErr, setMembersErr] = useState(null);

  const [groups, setGroups] = useState(null);
  const [groupsErr, setGroupsErr] = useState(null);
  const [groupsUnavailable, setGroupsUnavailable] = useState(false);

  const [tab, setTab] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadAccount = useCallback(() => {
    setAccountErr(null);
    getAccount(accountId)
      .then(setAccount)
      .catch((e) =>
        setAccountErr(
          e.status === 403
            ? "You don't have permission to view this account."
            : e.status === 404
              ? "Account not found."
              : `Failed to load account: ${e.message}`
        )
      );
  }, [accountId]);

  const loadMembers = useCallback(() => {
    setMembersErr(null);
    getAccountMembers(accountId)
      .then(setMembers)
      .catch((e) => {
        // 404 on this endpoint just means "no members surface yet"
        // — show the EmptyState instead of an error banner.
        if (e && e.status === 404) {
          setMembers([]);
          return;
        }
        setMembersErr(
          e.status === 403
            ? "You don't have permission to view members of this account."
            : `Failed to load members: ${e.message}`
        );
      });
  }, [accountId]);

  const loadGroups = useCallback(() => {
    setGroupsErr(null);
    setGroupsUnavailable(false);
    getAccountGroups(accountId, { fallbackFilter: true })
      .then((data) => setGroups(data))
      .catch((e) => {
        if (e && e.status === 404) {
          // Both /accounts/{aid}/groups AND the /groups fallback 404'd —
          // treat as "endpoint not yet available" per the graceful-404
          // rule.
          setGroups([]);
          setGroupsUnavailable(true);
          return;
        }
        setGroupsErr(
          e.status === 403
            ? "You don't have permission to view groups in this account."
            : `Failed to load groups: ${e.message}`
        );
      });
  }, [accountId]);

  useEffect(() => {
    loadAccount();
    loadMembers();
    loadGroups();
  }, [loadAccount, loadMembers, loadGroups]);

  const status = useMemo(() => trialStatus(account), [account]);
  const memberCount = Array.isArray(members) ? members.length : null;
  const groupCount = Array.isArray(groups) ? groups.length : null;
  const projectCount =
    typeof account?.projectCount === "number"
      ? account.projectCount
      : Array.isArray(account?.projects)
        ? account.projects.length
        : null;

  const copyId = () => {
    if (!accountId) return;
    try {
      navigator.clipboard?.writeText(accountId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — silent no-op */
    }
  };

  const handleInvited = ({ email }) => {
    setInviteOpen(false);
    setToast({ message: `Invited ${email || "user"} to this account.` });
    loadMembers();
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      await removeAccountMember(accountId, removing.userId || removing.id);
      setToast({ message: `Removed ${removing.email || "member"}.` });
      setRemoving(null);
      loadMembers();
    } catch (e) {
      setToast({
        message: `Failed to remove member: HTTP ${e.status || "?"}`,
        severity: "error",
      });
      setRemoving(null);
    }
  };

  const inviteButton = (
    <Tooltip
      title="Send an invitation to join this account. Recipient gets an email + set-password link."
      arrow
      enterDelay={400}
    >
      <span>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteOpen(true)}
          sx={{ textTransform: "none" }}
          aria-label="Invite user to account"
          disabled={!!accountErr}
        >
          Invite user to account
        </Button>
      </span>
    </Tooltip>
  );

  const loading = !account && !accountErr;

  return (
    <DetailShell
      backTo="/directory/accounts"
      parentLabel="Accounts"
      parentTo="/directory/accounts"
      title={account?.name || (loading ? "Loading…" : "Account")}
      avatar={
        account && (
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "primary.main",
            }}
          >
            <BusinessOutlinedIcon />
          </Avatar>
        )
      }
      badges={
        account
          ? [
              account.beta ? (
                <Tooltip
                  key="beta"
                  title="Beta account — trial-clock enforcement is skipped and the beta feature-flag is set."
                  arrow
                  enterDelay={400}
                >
                  <Chip
                    label="Beta"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Tooltip>
              ) : null,
              <StatusPill
                key="status"
                status={account.enabled === false ? "disabled" : "active"}
              />,
              <Chip
                key="trial"
                label={status.label}
                size="small"
                color={status.color}
                variant="outlined"
              />,
            ]
          : []
      }
      description={
        account ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: "wrap" }}
            component="span"
          >
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {account.id}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy account ID"}>
              <IconButton
                size="small"
                onClick={copyId}
                aria-label="Copy account ID"
              >
                <ContentCopyOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Box component="span" sx={{ color: "text.disabled" }}>
              ·
            </Box>
            <Typography component="span" variant="body2" color="text.secondary">
              Trial ends {formatDate(account.trialEndOn)}
            </Typography>
            {typeof account.maxProjects === "number" && (
              <>
                <Box component="span" sx={{ color: "text.disabled" }}>
                  ·
                </Box>
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  Max projects {account.maxProjects}
                </Typography>
              </>
            )}
            {projectCount !== null && (
              <>
                <Box component="span" sx={{ color: "text.disabled" }}>
                  ·
                </Box>
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {projectCount} projects
                </Typography>
              </>
            )}
          </Stack>
        ) : loading ? undefined : (
          "—"
        )
      }
      actions={inviteButton}
    >
      {accountErr && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {accountErr}
        </Alert>
      )}

      {loading && (
        <SkeletonTable columns={MEMBER_COLUMNS} rows={5} />
      )}

      {account && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="Account detail tabs"
            >
              <Tab
                label={
                  memberCount === null
                    ? "Users"
                    : `Users (${memberCount})`
                }
                id="account-tab-users"
                aria-controls="account-panel-users"
                title="People with membership in this account — see role and per-project grants."
              />
              <Tab
                label={
                  groupCount === null || groupsUnavailable
                    ? "Groups"
                    : `Groups (${groupCount})`
                }
                id="account-tab-groups"
                aria-controls="account-panel-groups"
                title="Groups scoped to this account — role bundles reused across many users."
              />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Box
              role="tabpanel"
              id="account-panel-users"
              aria-labelledby="account-tab-users"
            >
              <UsersPanel
                members={members}
                error={membersErr}
                onInvite={() => setInviteOpen(true)}
                onRemove={(m) => setRemoving(m)}
              />
            </Box>
          )}

          {tab === 1 && (
            <Box
              role="tabpanel"
              id="account-panel-groups"
              aria-labelledby="account-tab-groups"
            >
              <GroupsPanel
                groups={groups}
                error={groupsErr}
                unavailable={groupsUnavailable}
              />
            </Box>
          )}
        </>
      )}

      <InviteUserDialog
        open={inviteOpen}
        accountId={accountId}
        onClose={() => setInviteOpen(false)}
        onSaved={handleInvited}
      />

      <RemoveMemberDialog
        member={removing}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || "success"}
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </DetailShell>
  );
}

function UsersPanel({ members, error, onInvite, onRemove }) {
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  if (members === null) {
    return <SkeletonTable columns={MEMBER_COLUMNS} rows={5} />;
  }
  if (members.length === 0) {
    return (
      <SectionCard
        title="Users"
        isEmpty
        emptyState={{
          title: "No users in this account yet",
          description:
            "Invite the first teammate to collaborate — they will get an email link they can redeem to join.",
          primaryAction: (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={onInvite}
              sx={{ textTransform: "none" }}
            >
              Invite user
            </Button>
          ),
        }}
      />
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: dirTokens.radius }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {MEMBER_COLUMNS.map((c, i) => (
              <TableCell
                key={c.label || `col-${i}`}
                align={c.align || "left"}
                sx={{ width: c.width }}
              >
                {c.info ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent={c.align === "center" ? "center" : "flex-start"}
                    component="span"
                  >
                    <span>{c.label}</span>
                    <Tooltip title={c.info} arrow enterDelay={400}>
                      <InfoOutlinedIcon
                        fontSize="inherit"
                        sx={{
                          fontSize: 14,
                          color: "text.disabled",
                          verticalAlign: "middle",
                          cursor: "help",
                        }}
                      />
                    </Tooltip>
                  </Stack>
                ) : (
                  c.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((m) => {
            const uid = m.userId || m.id;
            const added = m.grantedOn || m.addedOn || m.createdOn;
            return (
              <TableRow key={uid || m.email} hover>
                <TableCell>
                  {uid ? (
                    <MuiLink
                      component={RouterLink}
                      to={`/directory/users/${uid}`}
                      underline="hover"
                    >
                      {m.email || "—"}
                    </MuiLink>
                  ) : (
                    m.email || "—"
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {m.name || m.displayName || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={m.role || "MEMBER"}
                    size="small"
                    color={
                      m.role === "ACCOUNT_ADMIN" ? "primary" : "default"
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  {m.canCreateProjects ? (
                    <Chip label="Yes" size="small" color="success" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(added)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title="Remove from account — revokes access to every project immediately."
                    arrow
                    enterDelay={400}
                  >
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemove(m)}
                      aria-label={`Remove ${m.email || "member"} from account`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function GroupsPanel({ groups, error, unavailable }) {
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  if (unavailable) {
    return (
      <SectionCard
        title="Groups"
        isEmpty
        emptyState={{
          title: "Groups endpoint not yet available",
          description:
            "Coord hasn't shipped account-scoped groups on this deployment. Use the top-level Groups tab in the meantime.",
        }}
      />
    );
  }
  if (groups === null) {
    return <SkeletonTable columns={GROUP_COLUMNS} rows={4} />;
  }
  if (groups.length === 0) {
    return (
      <SectionCard
        title="Groups"
        isEmpty
        emptyState={{
          title: "No groups in this account yet",
          description:
            "Create a group from the Groups tab to bundle roles — easier than granting roles per user.",
        }}
      />
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: dirTokens.radius }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {GROUP_COLUMNS.map((c) => (
              <TableCell
                key={c.label}
                align={c.align || "left"}
                sx={c.mono ? { fontFamily: "monospace", width: c.width } : { width: c.width }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g) => (
            <TableRow key={g.id} hover>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WorkspacesOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                  <MuiLink
                    component={RouterLink}
                    to={`/directory/groups`}
                    underline="hover"
                    state={{ focusGroupId: g.id }}
                  >
                    <Typography variant="body2" fontWeight={500} component="span">
                      {g.name || g.id}
                    </Typography>
                  </MuiLink>
                </Stack>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {g.description || "—"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <span
                  style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}
                >
                  {typeof g.memberCount === "number"
                    ? g.memberCount
                    : Array.isArray(g.members)
                      ? g.members.length
                      : "—"}
                </span>
              </TableCell>
              <TableCell>
                {g.accountId ? (
                  <ScopeBadge scope="account" name={g.accountName} />
                ) : (
                  <ScopeBadge scope="system" />
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              >
                {g.id}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function InviteUserDialog({ open, accountId, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [canCreate, setCanCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setRole("MEMBER");
    setCanCreate(false);
    setErr(null);
  }, [open]);

  useEffect(() => {
    if (role === "ACCOUNT_ADMIN") setCanCreate(true);
  }, [role]);

  const submit = async () => {
    if (!email) return;
    setBusy(true);
    setErr(null);
    try {
      await inviteUserToAccount(accountId, {
        email,
        role,
        canCreateProjects: canCreate,
      });
      onSaved && onSaved({ email });
    } catch (e) {
      setErr(
        e.status === 409
          ? "That email is already a member of this account."
          : `Failed to invite: HTTP ${e.status || "?"} — ${e.message}`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Invite user to account</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <FormControl fullWidth>
            <InputLabel>Account role</InputLabel>
            <Select
              label="Account role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <MenuItem value="MEMBER">Member</MenuItem>
              <MenuItem value="ACCOUNT_ADMIN">Account Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={canCreate}
                onChange={(e) => setCanCreate(e.target.checked)}
                disabled={role === "ACCOUNT_ADMIN"}
              />
            }
            label={
              role === "ACCOUNT_ADMIN"
                ? "Can create workspaces (admins always can)"
                : "Can create new workspaces"
            }
          />
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !email}
        >
          {busy ? "Inviting…" : "Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveMemberDialog({ member, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!member) setBusy(false);
  }, [member]);
  if (!member) return null;
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove {member.email || "member"}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          They&apos;ll lose access to all workspaces in this account immediately.
          Their user record remains — you can re-invite them later.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={async () => {
            setBusy(true);
            await onConfirm();
          }}
          disabled={busy}
        >
          {busy ? "Removing…" : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

